// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { registrationTeamId, resolveRegistrationTeamNames, storedRegistrationTeamName } from "@/lib/rlTeams";
import { toast } from "sonner";
import { ChevronLeft, Send } from "lucide-react";
import { format } from "date-fns";
import SendVerificationReminderButton from "@/components/admin/verification/SendVerificationReminderButton";
import { formatCalendarRange } from "@/lib/dates";
import { toCsv, downloadCsv, csvFileName } from "@/lib/printPackCsv";
import { BasicRichTextEditor } from "@/components/editor/BasicRichTextEditor";


function fmtDateRange(start?: string | null, end?: string | null) {
  if (!start) return "";
  return formatCalendarRange(start, end, "");
}

// ─────────────────────────────────────────────────────────────
// Additional recipients: chips. Commas, semicolons, spaces and newlines
// all separate. Invalid entries stay visible and marked; they are never
// silently dropped and never block the send (the server skips them).
// ─────────────────────────────────────────────────────────────
const RECIPIENT_SPLIT = /[,;\s]+/;
export const isAdhocEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
export const isAdhocPhone = (s: string) => /^\+?\d{10,15}$/.test(String(s).replace(/[\s-]/g, ""));

function RecipientChipsInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const add = (raw: string) => {
    const parts = raw.split(RECIPIENT_SPLIT).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const lower = new Set(value.map((v) => v.toLowerCase()));
    const next = [...value];
    for (const p of parts) if (!lower.has(p.toLowerCase())) { next.push(p); lower.add(p.toLowerCase()); }
    onChange(next);
  };
  const handleChange = (raw: string) => {
    if (RECIPIENT_SPLIT.test(raw)) {
      const segs = raw.split(RECIPIENT_SPLIT);
      const tail = segs.pop() ?? "";
      add(segs.join(","));
      setDraft(tail);
    } else setDraft(raw);
  };
  const invalid = value.filter((v) => !isAdhocEmail(v) && !isAdhocPhone(v));
  return (
    <div>
      <label htmlFor="adhoc-recipients" className="block text-body-sm text-ink-primary font-medium mb-1.5">
        Additional recipients (comma-separated emails or phones)
      </label>
      <div className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-2 py-1.5 flex flex-wrap gap-1.5 items-center">
        {value.map((v) => {
          const bad = !isAdhocEmail(v) && !isAdhocPhone(v);
          return (
            <span
              key={v}
              data-testid="recipient-chip"
              data-invalid={bad || undefined}
              title={bad ? "Not a usable email or phone number. It will be skipped." : v}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-body-sm max-w-full break-all ${
                bad ? "border-destructive text-destructive bg-destructive/10" : "border-stroke-subtle text-ink-primary bg-surface-raised"
              }`}
            >
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => onChange(value.filter((x) => x !== v))}
                className="ml-0.5 leading-none opacity-70 hover:opacity-100"
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          id="adhoc-recipients"
          type="text"
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (RECIPIENT_SPLIT.test(text)) { e.preventDefault(); add(draft + text); setDraft(""); }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(draft); setDraft(""); }
            else if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => { add(draft); setDraft(""); }}
          placeholder={value.length ? "" : "extra@example.com, +15551234567"}
          className="flex-1 min-w-[10rem] bg-transparent outline-none px-1 py-0.5 text-body-md text-ink-primary"
        />
      </div>
      {invalid.length > 0 && (
        <p className="mt-1 text-caption text-destructive">
          {invalid.length === 1 ? `"${invalid[0]}" is not` : `${invalid.length} entries are not`} a usable email or phone
          number and will be skipped. The others will still be sent.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Audience Picker
// ─────────────────────────────────────────────────────────────
function AudiencePicker({ event, audience, setAudience }: any) {
  const eventId = event?.id;

  const { data: divisions = [] } = useQuery({
    queryKey: ["event-divisions-for-messages", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_divisions")
        .select("name")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order");
      return Array.from(new Set((data || []).map((d: any) => d.name).filter(Boolean)));
    },
  });

  const { data: pools = [] } = useQuery({
    queryKey: ["event-pools-for-messages", eventId],
    enabled: !!eventId && !!event?.registration_levels_enabled,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_registration_levels")
        .select("label, sort_order")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  const { data: teams = [] } = useQuery({
    queryKey: ["event-teams-for-messages", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_registrations_v2")
        .select("team_id, recruitlook_team_id, display_name_override, extras_summary, division, pool")
        .eq("event_id", eventId)
        .not("registration_status", "in", "(cancelled,canceled,refunded,withdrawn)");
      if (error) throw error;
      const rows = data || [];
      const names = await resolveRegistrationTeamNames(rows, "(unnamed)");
      return rows.map((row: any) => ({
        ...row,
        team_id: registrationTeamId(row),
        team_name: names[registrationTeamId(row)] || storedRegistrationTeamName(row, "(unnamed)"),
      }));
    },
  });

  const { data: subscriberCount } = useQuery({
    queryKey: ["event-subscriber-count", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { count } = await (supabase as any)
        .from("event_notification_subscribers")
        .select("id", { count: "exact", head: true })
        .eq("event_id", eventId)
        .is("unsubscribed_at", null);
      return count ?? 0;
    },
  });

  const toggleArr = (key: string, value: any) => {
    setAudience((prev: any) => {
      const list = prev[key] || [];
      const next = list.includes(value)
        ? list.filter((v: any) => v !== value)
        : [...list, value];
      return { ...prev, [key]: next };
    });
  };

  return (
    <section className="bg-surface-card border border-stroke-subtle rounded-token-xl p-6 space-y-5">
      <div>
        <h2 className="text-heading-3 text-ink-primary">1. Who's this going to?</h2>
        <p className="text-body-sm text-ink-tertiary mt-1">
          Pick one or more audience filters. Recipients = the union of all selected filters.
        </p>
      </div>

      {/* Everyone */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!audience.everyone}
          onChange={(e) => setAudience({ ...audience, everyone: e.target.checked })}
        />
        <span className="text-body-md text-ink-primary font-medium">
          Everyone (all registered teams' coaches)
        </span>
      </label>

      {/* Public notification signups — explicit, never swept in by Everyone */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!audience.subscribers}
          onChange={(e) => setAudience({ ...audience, subscribers: e.target.checked })}
        />
        <span className="text-body-md text-ink-primary font-medium">
          Subscribers (public "Get Notified" signups)
          {typeof subscriberCount === "number" ? ` — ${subscriberCount}` : ""}
        </span>
      </label>

      {/* By role removed: every team recipient today is a coach, so "Coaches"
          was the Everyone filter under another name. Bring it back when
          athletes and parents can actually be reached. */}


      {/* Divisions */}
      {divisions.length > 0 && (
        <div>
          <div className="text-body-sm text-ink-primary font-medium mb-2">By division</div>
          <div className="flex flex-wrap gap-2">
            {divisions.map((d: string) => (
              <label
                key={d}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-token-md border cursor-pointer ${
                  (audience.divisions || []).includes(d)
                    ? "bg-cyan-subtle border-cyan text-ink-primary"
                    : "bg-surface-page border-stroke-subtle text-ink-secondary"
                }`}
              >
                <input
                  type="checkbox"
                  checked={(audience.divisions || []).includes(d)}
                  onChange={() => toggleArr("divisions", d)}
                />
                <span className="text-body-sm">{d}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Pools */}
      {pools.length > 0 && (
        <div>
          <div className="text-body-sm text-ink-primary font-medium mb-2">By pool</div>
          <div className="flex flex-wrap gap-2">
            {pools.map((p: any) => (
              <label
                key={p.label}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-token-md border cursor-pointer ${
                  (audience.pools || []).includes(p.label)
                    ? "bg-cyan-subtle border-cyan text-ink-primary"
                    : "bg-surface-page border-stroke-subtle text-ink-secondary"
                }`}
              >
                <input
                  type="checkbox"
                  checked={(audience.pools || []).includes(p.label)}
                  onChange={() => toggleArr("pools", p.label)}
                />
                <span className="text-body-sm">{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Teams (collapsible / scroll) */}
      {teams.length > 0 && (
        <details className="group">
          <summary className="text-body-sm text-ink-primary font-medium cursor-pointer">
            By team ({teams.length} registered)
          </summary>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded-token-md border border-stroke-subtle text-body-sm text-ink-primary hover:bg-surface-sunken"
              onClick={() =>
                setAudience({ ...audience, team_ids: teams.map((t: any) => t.team_id).filter(Boolean) })
              }
            >
              All teams
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded-token-md border border-stroke-subtle text-body-sm text-ink-secondary hover:bg-surface-sunken"
              onClick={() => setAudience({ ...audience, team_ids: [] })}
            >
              Clear
            </button>
          </div>
          <div className="mt-2 max-h-48 overflow-y-auto border border-stroke-subtle rounded-token-md p-2 space-y-1">
            {teams.map((t: any) => (
              <label
                key={t.team_id}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-sunken cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={(audience.team_ids || []).includes(t.team_id)}
                  onChange={() => toggleArr("team_ids", t.team_id)}
                />
                <span className="text-body-sm text-ink-primary">
                  {t.team_name || "(unnamed)"}{" "}
                  <span className="text-caption text-ink-tertiary">
                    {t.division ? `· ${t.division}` : ""}
                    {t.pool ? ` · ${t.pool}` : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </details>
      )}

      {/* Free-text */}
      <RecipientChipsInput
        value={audience.free_text_emails || []}
        onChange={(next: string[]) => setAudience({ ...audience, free_text_emails: next })}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Template Picker
// ─────────────────────────────────────────────────────────────
// Only the event's own organisation's templates. Rows with no organisation
// (platform plumbing such as deploy_guard_alert) never match, because
// .eq() on a null organisation id matches nothing.
const NOT_BROADCASTABLE = new Set([
  "admin_issue_assignment", "admin_issue_comment", "coach_attendance_alert",
  "coach_invitation", "staff_invitation", "payment_receipt",
  "payment_plan_installment_receipt", "payment_plan_reminder", "payment_reminder_generic",
  "registration_confirmation", "player_claim_notification", "roster_confirmation_request",
  "roster_invitation", "roster_locked_notification", "schedule_change",
]);
function TemplatePickerInline({ organizationId, onSelect, selectedId }: any) {
  const { data: templates = [] } = useQuery({
    queryKey: ["message-templates-for-event", organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_templates")
        .select(
          "id, key, name, category, trigger_type, channels:message_template_channels(channel, subject, body_html, sms_body, push_title, push_body)"
        )
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .order("category", { ascending: true })
        .order("name", { ascending: true });
      return data || [];
    },
  });

  // organiser_* alerts go to the director, not to teams — never offer them.
  // NOT_BROADCASTABLE: a required value only exists inside the flow that sends it.
  const broadcastReady = templates.filter(
    (t: any) =>
      !String(t.key || "").startsWith("organiser_") &&
      !NOT_BROADCASTABLE.has(String(t.key || "")) &&
      (["operational", "campaign"].includes(t.category) || t.trigger_type === "broadcast")
  );

  // Two cards with the same name would be ambiguous. After the organisation
  // filter this should never happen; if it does, show the key so it's visible.
  const nameCounts = broadcastReady.reduce((m: Record<string, number>, t: any) => {
    m[t.name] = (m[t.name] || 0) + 1;
    return m;
  }, {});

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {broadcastReady.map((t: any) => (
        <div
          key={t.id}
          className={`bg-surface-page border rounded-token-lg p-3 transition-colors flex items-start justify-between gap-2 ${
            selectedId === t.id
              ? "border-cyan ring-1 ring-cyan"
              : "border-stroke-subtle hover:border-cyan"
          }`}
        >
          <button type="button" onClick={() => onSelect(t)} className="text-left flex-1">
            <div className="text-body-sm text-ink-primary font-medium">{t.name}</div>
            <div className="text-caption text-ink-tertiary mt-1 capitalize">
              {t.category}
              {nameCounts[t.name] > 1 && <span className="normal-case"> · {t.key}</span>}
            </div>
          </button>
          <Link
            to={`/admin/messages/templates/${t.id}`}
            className="text-caption text-cyan hover:underline shrink-0"
          >
            Edit
          </Link>
        </div>
      ))}
      {broadcastReady.length === 0 && (
        <p className="md:col-span-2 text-body-sm text-ink-tertiary text-center py-4">
          No broadcast templates available. Create one in Messages → Templates with category
          "operational" or "campaign".
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Compose Tab
// ─────────────────────────────────────────────────────────────
function ComposeTab({ event, eventId, onSent }: any) {
  const queryClient = useQueryClient();
  const [audience, setAudience] = useState<any>({
    everyone: false,
    roles: [],
    divisions: [],
    pools: [],
    team_ids: [],
    free_text_emails: [],
    subscribers: false,
  });
  const [mode, setMode] = useState<"template" | "adhoc">("template");
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [subject, setSubject] = useState("");
  // Only a subject the director typed overrides the template's own subject.
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [bodyHtml, setBodyHtml] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [channels, setChannels] = useState<string[]>(["email"]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [countShift, setCountShift] = useState<{ from: number; to: number } | null>(null);
  const [checkingCount, setCheckingCount] = useState(false);

  const { data: verificationSettings } = useQuery({
    queryKey: ["event-verification-settings-for-messages", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any).rpc("get_event_verification_settings", {
        p_event_id: eventId,
      });
      return data || null;
    },
  });


  const toggleChannel = (ch: string, on: boolean) => {
    setChannels((prev) => (on ? Array.from(new Set([...prev, ch])) : prev.filter((c) => c !== ch)));
  };

  const handleSelectTemplate = (tpl: any) => {
    setSelectedTemplate(tpl);
    const emailCh = (tpl.channels || []).find((c: any) => c.channel === "email");
    const smsCh = (tpl.channels || []).find((c: any) => c.channel === "sms");
    const pushCh = (tpl.channels || []).find((c: any) => c.channel === "push");
    setSubject(renderEventTags(emailCh?.subject || "", event));
    setSubjectEdited(false);
    setBodyHtml(emailCh?.body_html || "");
    setSmsBody(smsCh?.sms_body || "");
    setPushTitle(pushCh?.push_title || "");
    setPushBody(pushCh?.push_body || "");
  };

  // Resolve recipient count via the same RPC
  const filtersForRpc = useMemo(
    () => ({
      everyone: !!audience.everyone,
      roles: audience.roles || [],
      divisions: audience.divisions || [],
      pools: audience.pools || [],
      team_ids: audience.team_ids || [],
      subscribers: !!audience.subscribers,
    }),
    [audience]
  );

  const hasFilter =
    filtersForRpc.everyone ||
    filtersForRpc.roles.length ||
    filtersForRpc.divisions.length ||
    filtersForRpc.pools.length ||
    filtersForRpc.team_ids.length ||
    filtersForRpc.subscribers;

  const { data: resolved = [] } = useQuery({
    queryKey: ["resolve-audience", eventId, filtersForRpc],
    enabled: !!eventId && !!hasFilter,
    refetchInterval: 15000,
    // The app-wide default pauses the interval while the tab is hidden and
    // turns focus refetch off — that is why an email added in another tab
    // never showed up. Keep polling in the background and refetch on return.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: "always",
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("resolve_event_message_audience", {
        p_event_id: eventId,
        p_filters: filtersForRpc,
      });
      if (error) throw error;
      return data || [];
    },
  });

  // Same team selection as the resolver (shared DB function), minus the
  // email gate — so the count and this list can never drift.
  const { data: unreachable = [] } = useQuery({
    queryKey: ["unreachable-audience", eventId, filtersForRpc],
    enabled: !!eventId && !!hasFilter,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: "always",
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("event_message_unreachable_teams", {
        p_event_id: eventId,
        p_filters: filtersForRpc,
      });
      if (error) throw error;
      return data || [];
    },
  });
  const selectedTeamCount = unreachable[0]?.selected_count ?? 0;
  const { data: pickerTeams = [] } = useQuery({
    queryKey: ["event-teams-for-messages", eventId],
    enabled: false,
  });
  const teamNameById: Record<string, any> = {};
  for (const t of pickerTeams as any[]) teamNameById[t.team_id] = t;

  const adhocEmails = (audience.free_text_emails || []).filter((s: string) => isAdhocEmail(s));
  const adhocPhones = (audience.free_text_emails || []).filter((s: string) => !isAdhocEmail(s) && isAdhocPhone(s));

  const seen = new Set<string>();
  const allRecipients = [
    ...resolved,
    ...adhocEmails.map((e: string) => ({ email: e, phone: null, user_id: null, name: e })),
    ...adhocPhones.map((p: string) => ({ email: null, phone: p, user_id: null, name: p })),
  ].filter((r: any) => {
    const k = r.user_id || r.email || r.phone;
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const recipientCount = allRecipients.length;
  const recipientCountRef = useRef(recipientCount);
  recipientCountRef.current = recipientCount;

  // Re-read the audience right before confirming, so the number in the
  // confirm step is the number the send will use.
  const openConfirm = async () => {
    const shown = recipientCount;
    setCheckingCount(true);
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["resolve-audience", eventId, filtersForRpc], exact: true }),
        queryClient.refetchQueries({ queryKey: ["unreachable-audience", eventId, filtersForRpc], exact: true }),
      ]);
    } finally {
      setCheckingCount(false);
    }
    await new Promise((r) => setTimeout(r, 0));
    const now = recipientCountRef.current;
    setCountShift(now !== shown ? { from: shown, to: now } : null);
    setShowConfirm(true);
  };
  const recipientsWithEmail = allRecipients.filter((r: any) => !!r.email).length;
  const recipientsWithSms = allRecipients.filter((r: any) => !!r.phone).length;

  // Push reach can only be counted server-side: device_tokens is readable by
  // its owner alone, so the broadcast function resolves the same audience and
  // joins it to device_tokens for us. Nothing is sent in preview mode.
  const { data: reach } = useQuery({
    queryKey: ["broadcast-reach", eventId, filtersForRpc, audience.free_text_emails, selectedTemplate?.id, subjectEdited ? subject : "", bodyHtml],
    enabled: !!eventId && (!!hasFilter || (audience.free_text_emails || []).length > 0),
    queryFn: async () => {
      const { data, error } = await (supabase as any).functions.invoke("send-event-broadcast", {
        body: {
          event_id: eventId, audience, channels: [], preview: true,
          template_id: selectedTemplate?.id || null,
          subject, subject_edited: subjectEdited,
          body_html: bodyHtml || null,
        },
      });
      if (error) throw error;
      return data || null;
    },
  });

  const recipientsWithPush = reach?.push_count ?? 0;

  const validationWarnings: string[] = [];
  if (channels.includes("email") && recipientsWithEmail === 0)
    validationWarnings.push("Email is selected but no recipients have an email on file.");
  if (channels.includes("sms") && recipientsWithSms === 0)
    validationWarnings.push(
      "SMS is selected but no recipients have a phone number on file."
    );
  if (channels.includes("push") && recipientsWithPush === 0)
    validationWarnings.push(
      "Push is selected but nobody in this audience has the app installed and signed in."
    );
  if (channels.includes("email") && (!subject || !bodyHtml))
    validationWarnings.push("Email channel requires a subject and body.");
  if (channels.includes("sms") && !smsBody)
    validationWarnings.push("SMS channel requires a message body.");
  if (channels.includes("push") && !pushBody)
    validationWarnings.push("Push channel requires a message body.");

  const canSend =
    recipientCount > 0 &&
    channels.length > 0 &&
    (!channels.includes("email") || (subject && bodyHtml)) &&
    (!channels.includes("sms") || smsBody) &&
    (!channels.includes("push") || pushBody);

  const reachSummary = channels
    .map((c) =>
      c === "email"
        ? `${recipientsWithEmail} by email`
        : c === "sms"
        ? `${recipientsWithSms} by SMS`
        : c === "push"
        ? `${recipientsWithPush} by push`
        : null
    )
    .filter(Boolean)
    .join(", ");


  const smsSegments = Math.max(1, Math.ceil((smsBody.length || 1) / 160));
  const approxSmsCost = (recipientsWithSms * 0.0079 * smsSegments).toFixed(4);

  const handleSend = async () => {
    setSending(true);
    try {
      const { data, error } = await (supabase as any).functions.invoke("send-event-broadcast", {
        body: {
          event_id: eventId,
          audience,
          channels,
          template_id: selectedTemplate?.id || null,
          subject,
          subject_edited: subjectEdited,
          body_html: bodyHtml,
          sms_body: smsBody,
          push_title: pushTitle,
          push_body: pushBody,
        },
      });
      if (error) throw error;
      toast.success(
        `Queued ${data?.queued_count ?? 0} messages to ${data?.recipient_count ?? 0} recipients`
      );
      setShowConfirm(false);
      setSubject("");
      setSubjectEdited(false);
      setBodyHtml("");
      const b = data?.blocked || [];
      if (b.length) toast.error(`${b.length} not sent — a required value is missing: ${b.slice(0, 5).map((x: any) => `${x.recipient} (${x.missing.join(", ")})`).join("; ")}${b.length > 5 ? "…" : ""}`, { duration: 15000 });
      setSmsBody("");
      setPushTitle("");
      setPushBody("");
      setSelectedTemplate(null);
      setMode("template");
      queryClient.invalidateQueries({ queryKey: ["event-message-history", eventId] });
      onSent?.();
    } catch (err: any) {
      toast.error("Send failed: " + (err?.message || "unknown"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <AudiencePicker event={event} audience={audience} setAudience={setAudience} />

      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-body-sm text-ink-tertiary">Recipients</span>
          <span className="text-heading-3 text-ink-primary font-medium">
            {recipientCount} {recipientCount === 1 ? "person" : "people"}
          </span>
        </div>
        {unreachable.length > 0 && (
          <details data-testid="unreachable-teams">
            <summary className="text-body-sm text-destructive cursor-pointer">
              {selectedTeamCount} {selectedTeamCount === 1 ? "team" : "teams"} selected ·{" "}
              {unreachable.length} {unreachable.length === 1 ? "has" : "have"} no coach email and cannot be
              reached
            </summary>
            <ul className="mt-2 max-h-48 overflow-y-auto text-body-sm text-ink-secondary list-disc pl-5">
              {unreachable.map((u: any) => (
                <li key={u.registration_id}>
                  {teamNameById[u.team_id]?.team_name || "(unnamed)"}
                  {teamNameById[u.team_id]?.division ? ` · ${teamNameById[u.team_id].division}` : ""}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <section className="bg-surface-card border border-stroke-subtle rounded-token-xl p-6 space-y-5">
        <div>
          <h2 className="text-heading-3 text-ink-primary">2. What's the message?</h2>
          <p className="text-body-sm text-ink-tertiary mt-1">
            Start from a template or compose ad-hoc.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setMode("template")}
            className={`px-4 py-2 rounded-token-md text-body-sm transition-colors ${
              mode === "template"
                ? "bg-cyan text-cyan-foreground"
                : "bg-surface-page border border-stroke-subtle text-ink-primary hover:border-cyan"
            }`}
          >
            Use template
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("adhoc");
              setSelectedTemplate(null);
            }}
            className={`px-4 py-2 rounded-token-md text-body-sm transition-colors ${
              mode === "adhoc"
                ? "bg-cyan text-cyan-foreground"
                : "bg-surface-page border border-stroke-subtle text-ink-primary hover:border-cyan"
            }`}
          >
            Compose ad-hoc
          </button>
        </div>

        {mode === "template" ? (
          <TemplatePickerInline
            organizationId={event?.organization_id}
            onSelect={handleSelectTemplate}
            selectedId={selectedTemplate?.id}
          />
        ) : (
          <p className="text-body-sm text-ink-tertiary">Compose your message below.</p>
        )}

        <div className="space-y-4 pt-2 border-t border-stroke-subtle">
          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Email subject {channels.includes("email") && <span className="text-status-danger">*</span>}
            </label>
            <input
              type="text"
              value={subject}
              name="broadcast-subject"
              autoComplete="off"
              onChange={(e) => { setSubject(e.target.value); setSubjectEdited(true); }}
              placeholder="e.g. Weather delay: games start at 10am"
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
            />
          </div>

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Email body
            </label>
            <BasicRichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              variant="email"
              ariaLabel="Email body"
              placeholder="Hi {{coach_first_name}},"
            />
          </div>

          {selectedTemplate && reach?.fill && (
            <BroadcastFillPreview reach={reach} />
          )}

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              SMS body (max 1600 chars; 160 per segment)
            </label>
            <textarea
              value={smsBody}
              onChange={(e) => setSmsBody(e.target.value)}
              rows={3}
              maxLength={1600}
              placeholder="Weather delay: games start at 10am. Check email for details."
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
            />
            <p className="text-caption text-ink-tertiary mt-1">
              {smsBody.length} chars · {smsSegments} segments
            </p>
          </div>

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Push title
            </label>
            <input
              type="text"
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              placeholder="Weather delay"
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
            />
          </div>

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Push message
            </label>
            <textarea
              value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Games now start at 10am."
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
            />
            <p className="text-caption text-ink-tertiary mt-1">
              Only reaches people signed in to the app on their phone.
            </p>
          </div>
        </div>
      </section>

      {verificationSettings?.enabled && (
        <section className="bg-surface-card border border-stroke-subtle rounded-token-xl p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-body-md text-ink-primary font-medium">Player verification</div>
            <p className="text-body-sm text-ink-tertiary">
              Message every coach with unverified players and the unverified athletes.
            </p>
          </div>
          <SendVerificationReminderButton eventId={eventId} />
        </section>
      )}

      <section className="bg-surface-card border border-stroke-subtle rounded-token-xl p-6">
        <h2 className="text-heading-3 text-ink-primary mb-3">3. Send</h2>


        <div className="space-y-2 mb-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={channels.includes("email")}
              onChange={(e) => toggleChannel("email", e.target.checked)}
            />
            <span className="text-body-md text-ink-primary">📧 Email</span>
            <span className="text-caption text-ink-tertiary">
              ({recipientsWithEmail} recipients have email)
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={channels.includes("sms")}
              onChange={(e) => toggleChannel("sms", e.target.checked)}
            />
            <span className="text-body-md text-ink-primary">💬 SMS</span>
            <span className="text-caption text-ink-tertiary">
              ({recipientsWithSms} have a phone number)
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={channels.includes("push")}
              onChange={(e) => toggleChannel("push", e.target.checked)}
            />
            <span className="text-body-md text-ink-primary">🔔 Push</span>
            <span className="text-caption text-ink-tertiary">
              {recipientsWithPush === 0
                ? "(nobody here can be reached by push)"
                : `(${recipientsWithPush} have the app on their phone)`}
            </span>
          </label>
        </div>

        {channels.length > 0 && (
          <p className="text-body-sm text-ink-secondary mb-5">
            {recipientCount} {recipientCount === 1 ? "recipient" : "recipients"}
            {reachSummary ? `: ${reachSummary}.` : "."}
          </p>
        )}



        {validationWarnings.length > 0 && (
          <div className="bg-status-warning-subtle border border-status-warning rounded-token-lg p-4 mb-5">
            <div className="text-body-sm text-ink-primary font-medium mb-1">Before sending:</div>
            <ul className="list-disc pl-5 text-body-sm text-ink-secondary">
              {validationWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-stroke-subtle">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="bg-surface-page border border-stroke-subtle hover:border-cyan rounded-token-md px-4 py-2 text-body-sm text-ink-primary hover:text-cyan transition-colors"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={openConfirm}
            disabled={!canSend || checkingCount}
            className="bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2.5 rounded-token-md text-body-md font-medium transition-colors"
          >
            Send to {recipientCount} {recipientCount === 1 ? "recipient" : "recipients"}
          </button>
        </div>
      </section>

      {/* Preview modal */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-surface-card border border-stroke-subtle rounded-token-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-stroke-subtle flex items-center justify-between">
              <h3 className="text-heading-4 text-ink-primary">Preview</h3>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="text-ink-tertiary hover:text-ink-primary"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">
              {channels.includes("email") && (
                <div>
                  <div className="text-caption text-ink-tertiary uppercase mb-1">Email subject</div>
                  <div className="text-body-md text-ink-primary font-medium mb-3">{subject}</div>
                  <div
                    className="prose prose-sm max-w-none border border-stroke-subtle rounded-token-md p-4 bg-surface-page"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  />
                </div>
              )}
              {channels.includes("sms") && (
                <div>
                  <div className="text-caption text-ink-tertiary uppercase mb-1">SMS</div>
                  <div className="bg-surface-page border border-stroke-subtle rounded-token-md p-4 text-body-md text-ink-primary whitespace-pre-wrap">
                    {smsBody}
                  </div>
                </div>
              )}
              {channels.includes("push") && (
                <div>
                  <div className="text-caption text-ink-tertiary uppercase mb-1">Push</div>
                  <div className="bg-surface-page border border-stroke-subtle rounded-token-md p-4">
                    <div className="text-body-md text-ink-primary font-medium">{pushTitle}</div>
                    <div className="text-body-md text-ink-secondary whitespace-pre-wrap">{pushBody}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-stroke-subtle rounded-token-xl max-w-md w-full p-6">
            <h3 className="text-heading-3 text-ink-primary mb-2">Confirm send</h3>
            {countShift && (
              <p data-testid="count-changed" className="text-body-sm text-status-warning mb-3">
                The recipient count changed from {countShift.from} to {countShift.to} since the button
                was drawn. This send will go to {countShift.to}.
              </p>
            )}
            <p className="text-body-md text-ink-secondary mb-4">
              You're about to send to <strong>{recipientCount}</strong>{" "}
              {recipientCount === 1 ? "person" : "people"} via {channels.join(" + ")}.
            </p>
            {channels.includes("sms") && recipientsWithSms > 0 && (
              <p className="text-body-sm text-ink-tertiary mb-4">
                Approximate SMS cost: ${approxSmsCost} ({recipientsWithSms} ×{" "}
                {smsSegments} segment{smsSegments === 1 ? "" : "s"} × $0.0079).
              </p>
            )}
            <p className="text-body-sm text-status-warning mb-5">This action can't be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="bg-surface-page border border-stroke-subtle px-4 py-2 rounded-token-md text-body-sm text-ink-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 px-5 py-2 rounded-token-md text-body-sm font-medium"
              >
                {sending ? "Sending..." : "Send Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// History Tab
// ─────────────────────────────────────────────────────────────
const STATUS_LABEL: Record<string, string> = {
  delivered: "Delivered",
  sent: "Sent",
  queued: "Queued",
  scheduled: "Scheduled",
  failed: "Failed",
  bounced: "Bounced",
  dropped: "Dropped",
  opened: "Opened",
  clicked: "Clicked",
  no_recipients: "No recipients",
};
const statusLabel = (s: string) =>
  STATUS_LABEL[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "—");
const statusClass = (s: string) =>
  ["delivered", "sent", "opened", "clicked"].includes(s)
    ? "text-status-success"
    : ["failed", "bounced", "dropped", "no_recipients"].includes(s)
    ? "text-status-danger"
    : "text-status-warning";
const prettyKey = (k: string | null) =>
  !k || k === "ad_hoc"
    ? "Custom message"
    : k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** Rows from one press of Send become one entry. Older rows without a
 *  broadcast id are grouped by message + channel + a 2-minute window. */
function groupHistory(rows: any[]) {
  const groups: any[] = [];
  const byKey = new Map<string, any>();
  for (const r of rows) {
    const t = new Date(r.sent_at || r.scheduled_at).getTime();
    let g: any = null;
    if (r.broadcast_id) {
      g = byKey.get("b:" + r.broadcast_id);
    } else {
      g = groups.find(
        (x) =>
          !x.broadcast_id &&
          x.template_key === r.template_key &&
          x.channel === r.channel &&
          x.isScheduled === (r.status === "scheduled") &&
          Math.abs(x.time - t) <= 120_000
      );
    }
    if (!g) {
      g = {
        id: r.broadcast_id || r.id,
        broadcast_id: r.broadcast_id,
        template_key: r.template_key,
        name: r.template_name || r.subject || prettyKey(r.template_key),
        channel: r.channel,
        time: t,
        isScheduled: r.status === "scheduled",
        rows: [],
      };
      groups.push(g);
      if (r.broadcast_id) byKey.set("b:" + r.broadcast_id, g);
    }
    g.rows.push(r);
    if (t > g.time) g.time = t;
  }
  return groups.sort((a, b) => b.time - a.time);
}

function HistoryTab({ eventId }: any) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const { data: history = [], isLoading, error } = useQuery({
    queryKey: ["event-message-history", eventId],
    enabled: !!eventId,
    refetchInterval: 15000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).functions.invoke("send-event-broadcast", {
        body: { event_id: eventId, action: "history" },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "History could not be loaded.");
      return data.rows || [];
    },
  });

  if (isLoading) {
    return (
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center text-body-md text-ink-tertiary">
        Loading history...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center text-body-md text-status-danger">
        History could not be loaded. {(error as any)?.message || ""}
      </div>
    );
  }
  if (history.length === 0) {
    return (
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center">
        <p className="text-body-md text-ink-tertiary">No messages sent for this event yet.</p>
      </div>
    );
  }

  const groups = groupHistory(history);
  const channelLabel = (c: string) => (c === "email" ? "Email" : c === "sms" ? "SMS" : c === "push" ? "Push" : c);

  return (
    <div className="bg-surface-card border border-stroke-subtle rounded-token-xl overflow-x-auto">
      <table className="w-full">
        <thead className="bg-surface-sunken">
          <tr className="text-left text-caption text-ink-tertiary uppercase tracking-wide">
            <th className="px-4 py-3">Sent</th>
            <th className="px-4 py-3">Message</th>
            <th className="px-4 py-3">Channel</th>
            <th className="px-4 py-3">To</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const real = g.rows.filter((r: any) => r.status !== "no_recipients");
            const counts: Record<string, number> = {};
            for (const r of real) counts[r.status] = (counts[r.status] || 0) + 1;
            const multi = real.length > 1;
            const isOpen = !!open[g.id];
            const when = g.isScheduled ? "Scheduled " : "";
            return (
              <React.Fragment key={g.id}>
                <tr
                  className={`border-t border-stroke-subtle ${multi ? "cursor-pointer hover:bg-surface-sunken" : ""}`}
                  onClick={() => multi && setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}
                >
                  <td className="px-4 py-3 text-body-sm text-ink-secondary whitespace-nowrap">
                    {when}
                    {format(new Date(g.time), "MMM d, h:mm a")}
                  </td>
                  <td className="px-4 py-3 text-body-sm text-ink-primary">{g.name}</td>
                  <td className="px-4 py-3 text-body-sm text-ink-secondary">{channelLabel(g.channel)}</td>
                  <td className="px-4 py-3 text-body-sm text-ink-secondary truncate max-w-[260px]">
                    {real.length === 0
                      ? "No recipients"
                      : multi
                      ? `${real.length} recipients ${isOpen ? "▾" : "▸"}`
                      : real[0].recipient || "—"}
                  </td>
                  <td className="px-4 py-3 text-body-sm">
                    {real.length === 0 ? (
                      <span className="text-status-danger">No recipients</span>
                    ) : multi ? (
                      Object.entries(counts)
                        .map(([s, n]) => `${n} ${statusLabel(s).toLowerCase()}`)
                        .join(", ")
                    ) : (
                      <span className={statusClass(real[0].status)} title={real[0].error_message || ""}>
                        {statusLabel(real[0].status)}
                        {real[0].status === "scheduled" && real[0].scheduled_at
                          ? ` · ${format(new Date(real[0].scheduled_at), "MMM d, h:mm a")}`
                          : ""}
                      </span>
                    )}
                  </td>
                </tr>
                {multi &&
                  isOpen &&
                  real.map((r: any) => (
                    <tr key={r.id} className="bg-surface-sunken/50 border-t border-stroke-subtle">
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2" />
                      <td className="px-4 py-2 text-body-sm text-ink-secondary truncate max-w-[260px]">
                        {r.recipient || "—"}
                      </td>
                      <td className="px-4 py-2 text-body-sm">
                        <span className={statusClass(r.status)} title={r.error_message || ""}>
                          {statusLabel(r.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Notification signups (public "Get Notified" list) — operator only
// ─────────────────────────────────────────────────────────────
function SubscribersTab({ eventId, eventName }: any) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["event-notification-subscribers", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_notification_subscribers")
        .select(
          "id, name, email, phone, wants_email, wants_sms, registration_id, unsubscribed_at, created_at"
        )
        .eq("event_id", eventId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const list = data || [];
      const regIds = Array.from(
        new Set(list.map((r: any) => r.registration_id).filter(Boolean))
      );
      let names: Record<string, string> = {};
      if (regIds.length) {
        const { data: regs } = await (supabase as any)
          .from("event_registrations_v2")
          .select("id, team_id, recruitlook_team_id, display_name_override")
          .in("id", regIds);
        const resolved = await resolveRegistrationTeamNames(regs || [], "(unnamed)");
        for (const reg of regs || []) {
          names[reg.id] =
            resolved[registrationTeamId(reg)] || storedRegistrationTeamName(reg, "(unnamed)");
        }
      }
      return list.map((r: any) => ({
        ...r,
        team_label: r.registration_id ? names[r.registration_id] || "(unnamed)" : "Whole event",
      }));
    },
  });

  const exportCsv = () => {
    const csv = toCsv(
      ["Name", "Email", "Phone", "Email updates", "Text updates", "Team followed", "Signed up", "Unsubscribed"],
      rows.map((r: any) => [
        r.name || "",
        r.email || "",
        r.phone || "",
        r.wants_email ? "Yes" : "No",
        r.wants_sms ? "Yes" : "No",
        r.team_label,
        r.created_at ? format(new Date(r.created_at), "yyyy-MM-dd HH:mm") : "",
        r.unsubscribed_at ? format(new Date(r.unsubscribed_at), "yyyy-MM-dd HH:mm") : "",
      ].map((v) => (/[",\n\r]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v))))
    );
    downloadCsv(csvFileName(eventName, "notification-signups"), csv);
  };

  if (isLoading) {
    return (
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center text-body-md text-ink-tertiary">
        Loading signups...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center">
        <p className="text-body-md text-ink-tertiary">
          Nobody has signed up for updates on this event yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={exportCsv}
          className="px-3 py-2 rounded-token-md border border-stroke-subtle text-body-sm text-ink-secondary hover:text-ink-primary"
        >
          Download CSV
        </button>
      </div>
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-sunken">
            <tr className="text-left text-caption text-ink-tertiary uppercase tracking-wide">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Following</th>
              <th className="px-4 py-3">Signed up</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} className="border-t border-stroke-subtle">
                <td className="px-4 py-3 text-body-sm text-ink-primary">{r.name || "—"}</td>
                <td className="px-4 py-3 text-body-sm text-ink-secondary">
                  {[r.wants_email ? r.email : null, r.wants_sms ? r.phone : null]
                    .filter(Boolean)
                    .join(" · ") || r.email || r.phone || "—"}
                </td>
                <td className="px-4 py-3 text-body-sm text-ink-secondary">{r.team_label}</td>
                <td className="px-4 py-3 text-body-sm text-ink-secondary whitespace-nowrap">
                  {r.created_at ? format(new Date(r.created_at), "MMM d, h:mm a") : "—"}
                </td>
                <td className="px-4 py-3 text-body-sm">
                  {r.unsubscribed_at ? (
                    <span className="text-ink-tertiary">Unsubscribed</span>
                  ) : (
                    <span className="text-status-success">Subscribed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
export default function EventMessagesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"compose" | "history" | "subscribers">("compose");

  const { data: event } = useQuery({
    queryKey: ["event-messages-event", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("events")
        .select("id, name, slug, start_date, end_date, registration_levels_enabled, organization_id")
        .eq("id", eventId)
        .maybeSingle();
      return data;
    },
  });

  return (
    <div className="bg-surface-page min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <Link
            to={`/admin-dashboard/events/${eventId}/edit`}
            className="inline-flex items-center gap-1 text-body-sm text-ink-tertiary hover:text-ink-primary mb-3"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to event
          </Link>
          <div className="flex items-center gap-3">
            <Send className="w-7 h-7 text-cyan" />
            <div>
              <h1 className="text-display-sm text-ink-primary font-display">Messages</h1>
              {event && (
                <p className="text-body-md text-ink-secondary mt-1">
                  {event.name} · {fmtDateRange(event.start_date, event.end_date)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 border-b border-stroke-subtle mb-6">
          {[
            { id: "compose", label: "Compose" },
            { id: "history", label: "History" },
            { id: "subscribers", label: "Notification signups" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id as any)}
              className={`px-4 py-3 -mb-px border-b-2 text-body-md transition-colors ${
                tab === t.id
                  ? "border-cyan text-ink-primary font-medium"
                  : "border-transparent text-ink-tertiary hover:text-ink-secondary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "compose" ? (
          <ComposeTab event={event} eventId={eventId} onSent={() => setTab("history")} />
        ) : tab === "history" ? (
          <HistoryTab eventId={eventId} />
        ) : (
          <SubscribersTab eventId={eventId} eventName={event?.name || "event"} />
        )}
      </div>
    </div>
  );
}

/** Fill {{event_name}} / {{event_dates}} so the subject box reads as it will be sent. */
function renderEventTags(text: string, event: any): string {
  const fmt = (d?: string | null) => {
    const [y, m, day] = String(d || "").slice(0, 10).split("-").map(Number);
    if (!y || !m || !day) return "";
    return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  };
  const a = fmt(event?.start_date), b = fmt(event?.end_date);
  const dates = a && b && a !== b ? `${a} – ${b}` : a || b;
  return String(text || "")
    .replace(/\{\{\s*event_name\s*\}\}/g, event?.name || "")
    .replace(/\{\{\s*event_dates\s*\}\}/g, dates || "");
}

/** How many recipients fill this template, and the email the first one gets. */
function BroadcastFillPreview({ reach }: any) {
  const f = reach.fill || {};
  const r = reach.rendered;
  return (
    <div className="space-y-2 rounded-token-md border border-stroke-subtle bg-surface-page p-3">
      <p className="text-body-sm text-ink-primary">
        <strong>{f.complete ?? 0}</strong> filled completely ·{" "}
        <strong>{f.missing_optional ?? 0}</strong> missing optional values ·{" "}
        <strong className={f.blocked ? "text-status-danger" : ""}>{f.blocked ?? 0}</strong> can't be sent (required value missing)
      </p>
      {(reach.blocked || []).length > 0 && (
        <p className="text-caption text-status-danger break-words">
          Not sent: {reach.blocked.slice(0, 5).map((b: any) => `${b.recipient} (${b.missing.join(", ")})`).join("; ")}
          {reach.blocked.length > 5 ? "…" : ""}
        </p>
      )}
      {r && (
        <div className="space-y-1">
          <div className="text-caption text-ink-tertiary uppercase">Preview for {r.recipient}</div>
          {(r.missing_optional || []).length > 0 && (
            <div className="text-caption text-ink-tertiary">Missing for this person: {r.missing_optional.join(", ")}</div>
          )}
          <div className="text-body-md text-ink-primary font-medium">{r.subject}</div>
          <iframe
            title="Email preview"
            srcDoc={r.html}
            sandbox=""
            className="w-full h-96 rounded-token-md border border-stroke-subtle bg-surface-raised"
          />
        </div>
      )}
    </div>
  );
}
